import React, { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import LeftAngle from "../../assets/image/left-angle.svg";
import RightAngle from "../../assets/image/right-angle.svg";
import DownloadIcon from "../../assets/image/download.svg";
import { getApiErrorMessage, handleApiCall } from "../../apis";
import DateRangeFilter from "../../Components/DateRangeFilter/DateRangeFilter";
import DateRangeAppliedLabel from "../../Components/DateRangeFilter/DateRangeAppliedLabel";
import { endpoint } from "../../utils/endpoints";
import Toastify, { notifyError, notifySuccess } from "../../utils/tostfy";
import { SEARCH_MAX_LENGTH, clampSearchInput } from "../../utils/search";
import {
  appendDateRangeParams,
  DATE_PARAM_KEYS,
  parseApiDate,
  toApiDate,
  validateDateRange,
} from "../../utils/dateRange";
import {
  loadListState,
  patchReturnToListQuery,
  saveListState,
} from "../../utils/listNavigation";
import CsvStatusBadge from "../../Components/CsvStatusBadge/CsvStatusBadge";
import { useAdminCsvReady } from "../../hooks/useAdminCsvReady";
import {
  isPaymentCsvType,
  patchSalaryCsvLogRow,
  resolveCsvStatusLabel,
} from "../../utils/adminCsvSocket";
import { formatDateTime } from "../../utils/formatDateTime";
import { useBackToList } from "../../hooks/useBackToList";
import { formatRupeesAbsolute as formatRupees } from "../../utils/formatAmount";
import PermissionGate from "../../Components/PermissionGate/PermissionGate";
import { MODULES } from "../../utils/permissions";
import PageNumberInput from "../../Components/Pagination/PageNumberInput";

const C = {
  border: "#E5E7EB",
  text: "#111827",
  muted: "#6B7280",
  primary: "#000053",
  tableHead: "#000053",
};

/** Values must match paymentHistory / getPaymentHistoryCSV API validation */
const PAYMENT_HISTORY_TYPE_OPTIONS = [
  { label: "All Types", value: "" },
  { label: "Normal Advance", value: "normal advance" },
  { label: "Salary Amount", value: "salary amount" },
  { label: "Award Amount", value: "award amount" },
  { label: "Trip Advance", value: "trip advance" },
];

const appendPaymentHistoryType = (params, typeValue) => {
  if (typeValue) params.set("type", typeValue);
};

const ViewTabButton = ({ active, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`md:h-10 h-9 lg:min-w-[7.5rem] rounded-md border px-6 text-sm font-UbuntuMedium transition-colors max-[991px]:!rounded-full ${
      active
        ? "border-[#000053] bg-[#000053] text-white"
        : "border-[#000053] bg-white text-[#000053] hover:bg-[#000053]/5"
    }`}
  >
    {label}
  </button>
);

function formatLogFilters(filter) {
  if (filter == null || filter === "") return "—";
  if (typeof filter === "string") return filter.trim() || "—";
  if (typeof filter !== "object") return "—";

  const parts = [];
  const typeVal =
    filter.type ?? filter.transaction_type ?? filter.transition_type ?? filter.verification_type;
  if (typeVal) {
    parts.push(`Transition Type: ${String(typeVal).replace(/_/g, " ")}`);
  }
  if (filter.from_date && filter.to_date) {
    parts.push(`From ${filter.from_date} to ${filter.to_date}`);
  } else if (filter.from_date) {
    parts.push(`From ${filter.from_date}`);
  } else if (filter.to_date) {
    parts.push(`To ${filter.to_date}`);
  }
  if (filter.month && filter.year) {
    parts.push(`Month: ${filter.month}/${filter.year}`);
  }
  if (filter.search) {
    parts.push(`Search: ${filter.search}`);
  }
  return parts.length ? parts.join(", ") : "—";
}

const mapLocation = (item) => ({
  id: item?._id || item?.id || "",
  name: item?.name || item?.location_name || "Unknown Location",
});

const mapPaymentHistoryRow = (r) => {
  const amount =
    (Number(r?.normal_advance) || 0) +
    (Number(r?.salary_amount) || 0) +
    (Number(r?.award_amount) || 0) +
    (Number(r?.trip_advance_amount) || 0);
  return {
    id: r?._id ?? "",
    dateTime: formatDateTime(r?.created_at),
    driverName: r?.full_name ?? "—",
    driverRefId: r?.driver_ref_id ?? "—",
    accountantName: r?.accountant_name ?? "—",
    employeeId: r?.employee_id ?? "—",
    amountPaid: formatRupees(amount),
    transitionType: r?.transition_type ?? "—",
    verificationType: r?.type ?? "—",
    reference: r?.trip_ref_id != null && r?.trip_ref_id !== "" ? `${r.trip_ref_id}` : "—",
    note:
      r?.note != null && String(r.note).trim() !== ""
        ? String(r.note).trim()
        : "—",
    previousBalance: formatRupees(r?.previous_balance),
    newBalance: formatRupees(r?.new_balance),
  };
};

const mapPaymentCsvLogRow = (item, index) => {
  const link = item?.csv_link || item?.file_url || item?.download_url || "";
  const fileName =
    item?.file_name ||
    item?.filename ||
    (link ? decodeURIComponent(String(link).split("/").pop()?.split("?")[0] || "") : "") ||
    "—";

  return {
    id: item?._id || item?.id || item?.csv_log_id || `log-${index}`,
    fileName,
    filters: formatLogFilters(item?.filter ?? item?.filters),
    createdAt: formatDateTime(item?.createdAt || item?.created_at),
    status: resolveCsvStatusLabel(item?.status, link),
    downloadUrl: link,
  };
};

const extractCsvLogList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.csv_log_data)) return data.csv_log_data;
  if (Array.isArray(data?.report_csv_log)) return data.report_csv_log;
  if (Array.isArray(data?.payment_history_csv_log)) return data.payment_history_csv_log;
  if (Array.isArray(data?.payment_csv_log)) return data.payment_csv_log;
  return [];
};

const ViewTransactions = () => {
  const { setSubheading } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const miplLocationId = searchParams.get("mipl_location_id") || "";
  const backToList = useBackToList("/salary-awards");
  const [activeView, setActiveView] = useState("listing");

  const [miplLocations, setMiplLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(true);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [pickerRange, setPickerRange] = useState([null, null]);
  const [type, setType] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(10);
  const [exportLoading, setExportLoading] = useState(false);

  const [logRows, setLogRows] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  const dateRangeCheck = validateDateRange(fromDate, toDate);
  const dateValidationError = dateRangeCheck.valid ? "" : dateRangeCheck.message;

  useEffect(() => {
    setSubheading("View Salary & Awards Details");
    return () => {
      setSubheading("");
    };
  }, [setSubheading]);

  useEffect(() => {
    setPickerRange([parseApiDate(fromDate), parseApiDate(toDate)]);
  }, [fromDate, toDate]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, fromDate, toDate, type, miplLocationId]);

  useEffect(() => {
    const fetchLocations = async () => {
      setLocationsLoading(true);
      try {
        const response = await handleApiCall("get", endpoint.listOfAlllocation, {});
        if (response?.data?.code === 200) {
          const d = response?.data?.data;
          const raw = Array.isArray(d)
            ? d
            : Array.isArray(d?.location_data)
              ? d.location_data
              : [];
          setMiplLocations(raw.map(mapLocation).filter((x) => x.id && x.name));
        } else {
          setMiplLocations([]);
        }
      } catch {
        setMiplLocations([]);
      } finally {
        setLocationsLoading(false);
      }
    };
    fetchLocations();
  }, []);

  const handleMiplLocationChange = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set("mipl_location_id", value);
    else next.delete("mipl_location_id");

    const returnTo = searchParams.get("returnTo");
    if (returnTo) {
      next.set(
        "returnTo",
        patchReturnToListQuery(returnTo, { mipl_location_id: value || "" })
      );
    }

    const savedListState = loadListState("/salary-awards") || {};
    const nextListState = { ...savedListState };
    if (value) nextListState.mipl_location_id = value;
    else delete nextListState.mipl_location_id;
    saveListState("/salary-awards", nextListState);

    setSearchParams(next, { replace: true });
    setCurrentPage(1);
  };

  const fetchPaymentHistory = useCallback(async () => {
    if (dateValidationError) {
      setRows([]);
      setListLoading(false);
      setTotalRecords(0);
      setTotalPages(1);
      return;
    }

    setListLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(currentPage),
        limit: String(itemsPerPage),
        search: clampSearchInput(debouncedSearch).trim(),
      });
      appendDateRangeParams(
        params,
        { start: fromDate, end: toDate },
        DATE_PARAM_KEYS.fromTo
      );
      appendPaymentHistoryType(params, type);
      if (miplLocationId) params.set("mipl_location_id", miplLocationId);
      const response = await handleApiCall(
        "get",
        `${endpoint.paymentHistory}?${params.toString()}`,
        {}
      );

      if (response?.data?.code === 200) {
        const d = response?.data?.data || {};
        const list = Array.isArray(d?.payment_history_data) ? d.payment_history_data : [];
        setRows(list.map(mapPaymentHistoryRow));
        setTotalRecords(d?.total ?? list.length);
        setTotalPages(Math.max(1, d?.total_pages ?? 1));
      } else {
        setRows([]);
        setTotalRecords(0);
        setTotalPages(1);
        notifyError(
          getApiErrorMessage(response, "Failed to load payment history")
        );
      }
    } catch {
      setRows([]);
      setTotalRecords(0);
      setTotalPages(1);
      notifyError("Failed to load payment history");
    } finally {
      setListLoading(false);
    }
  }, [
    currentPage,
    itemsPerPage,
    debouncedSearch,
    fromDate,
    toDate,
    type,
    dateValidationError,
    miplLocationId,
  ]);

  const fetchPaymentCsvLog = useCallback(async () => {
    setLogLoading(true);
    try {
      const response = await handleApiCall("get", endpoint.getPaymentCsvLog, {});

      if (response?.data?.code === 200) {
        const d = response?.data?.data || {};
        const list = extractCsvLogList(d);
        setLogRows(list.map(mapPaymentCsvLogRow));
      } else {
        setLogRows([]);
        notifyError(getApiErrorMessage(response, "Failed to load CSV log"));
      }
    } catch {
      setLogRows([]);
      notifyError("Failed to load CSV log");
    } finally {
      setLogLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeView !== "listing") return;
    fetchPaymentHistory();
  }, [activeView, fetchPaymentHistory]);

  useEffect(() => {
    if (activeView !== "log") return;
    fetchPaymentCsvLog();
  }, [activeView, fetchPaymentCsvLog]);

  const handleCsvReady = useCallback(
    (payload) => {
      if (!isPaymentCsvType(payload?.type)) return;

      const logId = payload?.csv_log_id;
      setLogRows((prev) => {
        const idx = logId ? prev.findIndex((row) => row.id === logId) : -1;
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = patchSalaryCsvLogRow(prev[idx], payload);
          return next;
        }
        return prev;
      });
      fetchPaymentCsvLog();
    },
    [fetchPaymentCsvLog]
  );

  useAdminCsvReady(handleCsvReady);

  const hasListingData = !listLoading && totalRecords > 0;

  const handleExportCsv = async () => {
    if (!hasListingData) {
      notifyError("No payment history data to export");
      return;
    }
    setExportLoading(true);
    try {
      const params = new URLSearchParams();
      appendDateRangeParams(
        params,
        { start: fromDate, end: toDate },
        DATE_PARAM_KEYS.fromTo
      );
      const q = clampSearchInput(search).trim();
      if (q) params.set("search", q);
      appendPaymentHistoryType(params, type);
      if (miplLocationId) params.set("mipl_location_id", miplLocationId);

      const response = await handleApiCall(
        "get",
        `${endpoint.getPaymentHistoryCSV}?${params.toString()}`,
        {}
      );

      if (response?.data?.code === 200) {
        await fetchPaymentCsvLog();
        return;
      }

      notifyError(getApiErrorMessage(response, "Failed to export CSV"));
    } catch {
      notifyError("Failed to export CSV");
    } finally {
      setExportLoading(false);
    }
  };

  const handleApplyDates = () => {
    const [start, end] = pickerRange;
    if (!start || !end) {
      notifyError("Please select start and end dates");
      return false;
    }
    setFromDate(toApiDate(start));
    setToDate(toApiDate(end));
    setCurrentPage(1);
    return true;
  };

  const handleClearDates = () => {
    setPickerRange([null, null]);
    setFromDate("");
    setToDate("");
    setCurrentPage(1);
  };

  const clearListingFilters = () => {
    handleClearDates();
    setType("");
    setSearch("");
    handleMiplLocationChange("");
    setCurrentPage(1);
  };

  const hasDateRangeSelected = Boolean(fromDate && toDate);
  const hasAnyListingFilter = Boolean(
    hasDateRangeSelected ||
      type ||
      clampSearchInput(search).trim() ||
      miplLocationId ||
      pickerRange[0] ||
      pickerRange[1]
  );

  const handleDownloadLogFile = (url) => {
    if (!url) {
      notifyError("Download link not available");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const listingPage = Math.min(currentPage, totalPages);
  const listingFirstIndex = (listingPage - 1) * itemsPerPage;

  return (
    <>
      <Toastify />
      <div className="tab-pane-content md:mt-7 mt-3 font-UbuntuRegular">
        <Link to={backToList} className="btn btn-primary ml-auto md:mb-4 !mb-3">
          Back
        </Link>

        <div className="md:mb-4 !mb-3 flex flex-wrap md:gap-2 gap-1">
          <ViewTabButton
            active={activeView === "listing"}
            label="Listing"
            onClick={() => setActiveView("listing")}
          />
          <ViewTabButton
            active={activeView === "log"}
            label="Log CSV"
            onClick={() => setActiveView("log")}
          />
        </div>

        {activeView === "listing" && (
          <>
            <div className="md:mb-4 mb-2 flex flex-wrap items-center md:gap-x-4 md:gap-y-3 gap-x-2 gap-y-2 relative">
              <div className="inline-flex h-11 flex-nowrap items-center gap-2.5 absolute md:relative right-1 -top-14 max-[767px]:mt-1 md:top-0 max-[767px]:[&_.relative_button]:h-[36px] max-[767px]:[&_.relative_button]:w-[36px]  max-[767px]:[&_.relative_button_img]:h-4 max-[767px]:[&_.relative_.absolute]:right-1 max-[767px]:[&_.relative_.absolute]:!left-auto">
                <DateRangeFilter
                  variant="popover"
                  bare
                  toolbarIcon
                  range={pickerRange}
                  onRangeChange={setPickerRange}
                  onApply={handleApplyDates}
                  applyDisabled={!pickerRange[0] || !pickerRange[1]}
                  onClear={handleClearDates}
                  maxDate={new Date()}
                />
                {hasDateRangeSelected && (
                  <DateRangeAppliedLabel
                    from={fromDate}
                    to={toDate}
                    prefix="Date range: "
                    className="shrink-0 whitespace-nowrap"
                  />
                )}
              </div>

              {hasAnyListingFilter && (
                <button
                  type="button"
                  onClick={clearListingFilters}
                  className="h-10 shrink-0 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-4 text-sm font-UbuntuMedium text-gray-800 shadow-sm hover:border-gray-400 hover:bg-gray-50"
                >
                  Clear Filters
                </button>
              )}

              <div className="md:inline-flex md:h-11 shrink-0 md:items-center gap-2 flex flex-col md:flex-row items-start">
                <label
                  className="mb-0 whitespace-nowrap text-xs font-UbuntuMedium"
                  style={{ color: C.muted }}
                  htmlFor="payment-history-mipl-location"
                >
                  MIPL Location
                </label>
                <select
                  id="payment-history-mipl-location"
                  className="form-control h-10 min-w-[10rem] rounded-lg border text-sm"
                  style={{ borderColor: C.border }}
                  value={miplLocationId}
                  onChange={(e) => handleMiplLocationChange(e.target.value)}
                  disabled={locationsLoading}
                >
                  <option value="">
                    {locationsLoading ? "Loading locations…" : "All MIPL locations"}
                  </option>
                  {miplLocations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:inline-flex md:h-11 shrink-0 md:items-center gap-2 flex flex-col md:flex-row items-start">
                <label
                  className="mb-0 whitespace-nowrap text-xs font-UbuntuMedium"
                  style={{ color: C.muted }}
                >
                  Transition Type
                </label>
                <select
                  className="form-control h-10 min-w-[10rem] rounded-lg border text-sm"
                  style={{ borderColor: C.border }}
                  value={type}
                  onChange={(e) => {
                    setType(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  {PAYMENT_HISTORY_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value || "all"} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="inline-flex h-11 min-w-[12rem] flex-1 items-center gap-2 sm:max-w-xs">
                <label
                  className="mb-0 shrink-0 whitespace-nowrap text-xs font-UbuntuMedium md:inline-flex hidden"
                  style={{ color: C.muted }}
                >
                  Search Driver
                </label>
                <input
                  type="text"
                  className="searchIcon md:h-10 h-9 md:min-w-0 flex-1 rounded-lg border bg-white md:px-3 px-2 md:py-2 py-1.5 text-sm outline-none focus:!shadow-sm"
                  style={{ borderColor: C.border }}
                  placeholder="Search by name or ID..."
                  value={search}
                        maxLength={SEARCH_MAX_LENGTH}

                        onChange={(e) => setSearch(clampSearchInput(e.target.value))}
                />
              </div>

              <PermissionGate module={MODULES.SALARY_AWARDS} action="export">
                <button
                  type="button"
                  className="ml-auto md:h-10 h-9 md:px-8 px-6 shrink-0 rounded-md md:text-base text-sm font-UbuntuMedium text-white !bg-primary disabled:opacity-50"
                  style={{ background: C.primary }}
                  onClick={handleExportCsv}
                  disabled={exportLoading || !hasListingData}
                  title={
                    !hasListingData ? "Export is available when listing has data" : undefined
                  }
                >
                  {exportLoading ? "Exporting…" : "Export"}
                </button>
              </PermissionGate>
            </div>

            {dateValidationError ? (
              <p className="mb-3 text-sm text-red-500">{dateValidationError}</p>
            ) : null}

            <div className="common-table-wrapper m-0 rounded-xl border shadow-sm" style={{ borderColor: C.border }}>
              <div className="m-0 w-full overflow-x-auto">
                <table className="mb-0 w-full min-w-[1500px] bg-white">
                  <thead>
                    <tr>
                      {[
                        "S.No.",
                        "Date & Time",
                        "Driver Name",
                        "Driver ID",
                        "Accountant Name",
                        "Accountant Id",
                        "Amount Paid",
                        "Transition Type",
                        "Verification Type",
                        "Trip ID",
                        "Note",
                        "Previous Balance",
                        "New Balance",
                      ].map((title) => (
                        <th
                          key={title}
                          className={`whitespace-nowrap border-0 px-3 py-3.5 text-[13px] font-UbuntuMedium text-white ${
                            title === "Note" ? "text-center" : ""
                          }`}
                        >
                          {title}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="max-[767px]:[&_td]:!py-2">
                    {listLoading ? (
                      <tr>
                        <td colSpan={13} className="py-10 text-center" style={{ color: C.muted }}>
                          Loading...
                        </td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td colSpan={13} className="py-10 text-center" style={{ color: C.muted }}>
                          No transactions found.
                        </td>
                      </tr>
                    ) : (
                      rows.map((row, i) => (
                        <tr key={row.id || i} className={i % 2 ? "bg-[#FAFAFA]" : "bg-white"}>
                          <td className="whitespace-nowrap px-3 py-4">
                            {String(listingFirstIndex + i + 1).padStart(2, "0")}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4">{row.dateTime}</td>
                          <td className="whitespace-nowrap px-3 py-4">{row.driverName}</td>
                          <td className="whitespace-nowrap px-3 py-4">{row.driverRefId}</td>
                          <td className="whitespace-nowrap px-3 py-4">{row.accountantName}</td>
                          <td className="whitespace-nowrap px-3 py-4">{row.employeeId}</td>
                          <td className="whitespace-nowrap px-3 py-4">{row.amountPaid}</td>
                          <td className="whitespace-nowrap px-3 py-4">{row.transitionType}</td>
                          <td className="whitespace-nowrap px-3 py-4">{row.verificationType}</td>
                          <td className="whitespace-nowrap px-3 py-4">{row.reference}</td>
                          <td className="max-w-[220px] whitespace-normal break-words px-3 py-4 text-center">
                            {row.note}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4">{row.previousBalance}</td>
                          <td className="whitespace-nowrap px-3 py-4">{row.newBalance}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="lg:!mt-5 !mt-2 md:!mt-4 flex items-center justify-end md:gap-3 gap-2 text-lg">
              <span className="text-xs md:text-sm text-gray-500 md:ml-auto max-[767px]:mr-auto">
                {totalRecords} record{totalRecords === 1 ? "" : "s"} found
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={listingPage === 1 || listLoading}
                className="!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center "
              >
                <img src={LeftAngle} className="h-3 invert grayscale brightness-100" alt="" />
              </button>
              <PageNumberInput
                page={listingPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                className="h-[35px] w-10 md:w-12 rounded border border-gray-300 text-center text-sm font-UbuntuMedium shadow-sm outline-none disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70"
              />
              <span className="text-sm text-gray-600 text-nowrap">of {totalPages}</span>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={listingPage === totalPages || listLoading}
                className="!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center "
              >
                <img src={RightAngle} className="h-3 invert grayscale brightness-100" alt="" />
              </button>
            </div>
          </>
        )}

        {activeView === "log" && (
          <>
            <div className="common-table-wrapper m-0 rounded-xl border shadow-sm" style={{ borderColor: C.border }}>
              <div className="m-0 w-full overflow-x-auto">
                <table className="mb-0 w-full min-w-[900px] bg-white">
                  <thead>
                    <tr>
                      {["S.No.", "File Name", "Filters", "Created Date", "Status", "Action"].map((title) => (
                        <th
                          key={title}
                          className="whitespace-nowrap border-0 px-3 py-3.5 text-center text-[13px] font-UbuntuMedium text-white"
                        >
                          {title}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {logLoading ? (
                      <tr>
                        <td colSpan={6} className="py-10 text-center" style={{ color: C.muted }}>
                          Loading...
                        </td>
                      </tr>
                    ) : logRows.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-10 text-center" style={{ color: C.muted }}>
                          No CSV exports found.
                        </td>
                      </tr>
                    ) : (
                      logRows.map((row, i) => (
                        <tr key={row.id} className={i % 2 ? "bg-[#FAFAFA]" : "bg-white"}>
                          <td className="px-3 md:py-4 py-2 text-center">
                            {String(i + 1).padStart(2, "0")}
                          </td>
                          <td className="max-w-[240px] truncate px-3 md:py-4 py-2 text-center" title={row.fileName}>
                            {row.fileName}
                          </td>
                          <td className="px-3 md:py-4 py-2 text-center">{row.filters}</td>
                          <td className="whitespace-nowrap px-3 md:py-4 py-2 text-center">{row.createdAt}</td>
                          <td className="px-3 md:py-4 py-2 text-center">
                            <CsvStatusBadge status={row.status} />
                          </td>
                          <td className="px-3 md:py-4 py-2 text-center">
                            {row.status === "Complete" && row.downloadUrl ? (
                              <button
                                type="button"
                                onClick={() => handleDownloadLogFile(row.downloadUrl)}
                                className="inline-flex lg:h-9 lg:w-9 h-7 w-7 items-center justify-center rounded-full border-0 !bg-primary"
                                aria-label={`Download ${row.fileName}`}
                              >
                                <img src={DownloadIcon} alt="" className="lg:h-3.5 lg:w-3.5 h-3 w-3" />
                              </button>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="lg:!mt-5 !mt-2 md:!mt-4 flex justify-end text-lg">
              <span className="text-xs md:text-sm text-gray-500 md:ml-auto max-[767px]:mr-auto">
                {logRows.length} record{logRows.length === 1 ? "" : "s"} found
              </span>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ViewTransactions;
